import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Camera, FileUp, X } from 'lucide-react';
import { receptionsAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';

/**
 * Réception IA pour une consolidation : le fournisseur et les sites sont déjà
 * connus — on demande uniquement la facture (PDF ou photos). L'extraction IA
 * tourne puis onExtracted(prefill) est appelé avec les lignes lues.
 */
export default function ConsolidatedAIImportDialog({ open, onClose, supplierId, siteId, onExtracted }) {
    const [files, setFiles] = useState([]);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState('');
    const [percent, setPercent] = useState(0);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    const reset = () => { setFiles([]); setRunning(false); setProgress(''); setPercent(0); };

    const handleAnalyze = async () => {
        if (!files.length) { toast.error('Ajoutez la facture (PDF ou photos)'); return; }
        setRunning(true);
        setProgress('Envoi du document...');
        setPercent(2);
        try {
            const fd = new FormData();
            fd.append('supplier_id', supplierId);
            fd.append('site_id', siteId);
            const isPdf = files.length === 1 && files[0].type === 'application/pdf';
            if (isPdf) fd.append('pdf', files[0]);
            else files.forEach(f => fd.append('photos', f));
            const startRes = await receptionsAPI.importInvoicePreview(fd);
            const jobId = startRes.data.job_id;
            let result = null;
            for (let i = 0; i < 150; i++) {
                await new Promise(r => setTimeout(r, 2000));
                const st = await receptionsAPI.importInvoicePreviewStatus(jobId);
                setProgress(st.data.progress || '...');
                setPercent(st.data.percent || 0);
                if (st.data.status === 'done') { result = st.data.result; break; }
                if (st.data.status === 'error') throw new Error(st.data.error || 'Erreur extraction');
            }
            if (!result) throw new Error('Délai dépassé');

            // Agrège toutes les factures lues : qty par supplier_product_id
            const bySp = {};
            const unmatched = [];
            const invoiceNumbers = [];
            let invoiceDate = null;
            (result.invoices || []).forEach(inv => {
                if (inv.invoice_number) invoiceNumbers.push(inv.invoice_number);
                if (!invoiceDate && inv.invoice_date) invoiceDate = inv.invoice_date;
                (inv.items || []).forEach(it => {
                    const spId = it.matched_supplier_product_id;
                    if (spId) {
                        if (!bySp[spId]) bySp[spId] = { supplier_product_id: spId, qty: 0, unit_price: 0 };
                        bySp[spId].qty = Math.round((bySp[spId].qty + (it.quantity || 0)) * 1000) / 1000;
                        if (it.unit_price_ht) bySp[spId].unit_price = it.unit_price_ht;
                    } else {
                        unmatched.push({ description: it.description, qty: it.quantity, unit_price: it.unit_price_ht });
                    }
                });
            });
            const items = Object.values(bySp);
            if (!items.length) {
                toast.error('Aucune ligne de la facture n\'a pu être associée aux produits du fournisseur');
                setRunning(false);
                return;
            }
            if (unmatched.length) {
                toast.warning(`${unmatched.length} ligne(s) non reconnue(s) : ${unmatched.slice(0, 3).map(u => u.description).join(', ')}${unmatched.length > 3 ? '…' : ''}`);
            }
            toast.success(`${items.length} produit(s) lus sur la facture`);
            onExtracted({
                invoiceNumber: invoiceNumbers.join(' / '),
                receptionDate: invoiceDate || null,
                items,
                unmatched,
            });
            reset();
            onClose();
        } catch (e) {
            toast.error(e.message || 'Erreur pendant l\'analyse IA');
            setRunning(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !running) { reset(); onClose(); } }}>
            <DialogContent className="sm:max-w-md" data-testid="cons-ai-import-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-orange-500" /> Réception IA — consolidation
                    </DialogTitle>
                    <DialogDescription>
                        Fournisseur et sites déjà connus. Ajoutez simplement la facture : les quantités lues seront ventilées sur les commandes des sites, à corriger si besoin.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple className="hidden"
                            onChange={(e) => { setFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                        <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) setFiles(prev => [...prev.filter(x => x.type !== 'application/pdf'), f]); e.target.value = ''; }} />
                        <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => fileRef.current?.click()} disabled={running} data-testid="cons-ai-pick-file">
                            <FileUp className="w-3.5 h-3.5 mr-1" /> PDF / fichiers
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => cameraRef.current?.click()} disabled={running} data-testid="cons-ai-take-photo">
                            <Camera className="w-3.5 h-3.5 mr-1" /> Prendre une photo
                        </Button>
                    </div>
                    <PhoneCaptureButton
                        size="sm"
                        className="w-full text-xs"
                        label="Prendre avec le téléphone"
                        disabled={running}
                        testId="cons-ai-phone-btn"
                        onPhotos={(fs) => setFiles(prev => [...prev.filter(x => x.type !== 'application/pdf'), ...fs])}
                    />
                    {files.length > 0 && (
                        <div className="space-y-1">
                            {files.map((f, i) => (
                                <div key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                                    <span className="truncate">{f.name}</span>
                                    {!running && (
                                        <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-slate-400" /></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {running && (
                        <div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-orange-400 transition-all" style={{ width: `${percent}%` }} />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{progress}</p>
                        </div>
                    )}
                    <Button
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={handleAnalyze}
                        disabled={running || !files.length}
                        data-testid="cons-ai-analyze-btn"
                    >
                        {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Analyser la facture
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
